/* Collapsible, bilingual "how to get these values" panel shared by the File storage and Email settings pages.
 * Steps are numbered; every external link opens the vendor's official page in a new tab. */
import type { ReactNode } from "react";
import { useI18n } from "../../../i18n";
import { Icon } from "../../../ui";

export type Pair = [string, string];
export interface GuideLink { label: Pair; url: string }

/** External link to an official vendor page (new tab, no opener, no referrer). */
export function ExtLink({ link, testId }: { link: GuideLink; testId?: string }) {
  const { L } = useI18n();
  return (
    <a className="ext-link" href={link.url} target="_blank" rel="noopener noreferrer" data-testid={testId}>
      {L(link.label[0], link.label[1])}<Icon name="external" className="s" />
      <span className="sr">{L(" (opens in a new tab)", " (يفتح في تبويب جديد)")}</span>
    </a>
  );
}

export function LinkList({ links, testPrefix }: { links: GuideLink[]; testPrefix: string }) {
  if (!links.length) return null;
  return (
    <ul className="ext-links">
      {links.map((l, i) => <li key={l.url}><ExtLink link={l} testId={testPrefix + "-link-" + i} /></li>)}
    </ul>
  );
}

/** Numbered guide steps; each step is plain bilingual text or custom content (e.g. a copyable policy). */
export function GuideSteps({ steps, testPrefix }: { steps: ReactNode[]; testPrefix: string }) {
  return (
    <ol className="setup-steps" data-testid={testPrefix + "-steps"}>
      {steps.map((s, i) => <li key={i} data-testid={testPrefix + "-step-" + (i + 1)}><span className="num" aria-hidden="true">{i + 1}</span><div className="grow">{s}</div></li>)}
    </ol>
  );
}

/** The collapsible card. `open` is the initial state (open when the feature is not configured yet). */
export default function SetupGuide({ title, sub, open, testId, children }: { title: string; sub?: string; open: boolean; testId: string; children: ReactNode }) {
  return (
    <details className="card setup-guide" open={open} data-testid={testId}>
      <summary className="card-h" data-testid={testId + "-toggle"}>
        <div><h3><Icon name="book" className="s" /> {title}</h3>{sub && <p>{sub}</p>}</div>
        <Icon name="chevron-down" className="s chev" />
      </summary>
      <div className="card-b stack">{children}</div>
    </details>
  );
}
